/**
 * Tur / ürün yorumu puan kategorileri — backend language variable labelKey ile eşleşir.
 * Eski kayıtlar (value_for_money, destination, …) legacyKeys ile okunur.
 */

export const PRODUCT_REVIEW_RATING_CATEGORIES = [
    {
        key: 'overall_experience',
        labelKey: 'review_form.rating.overall_experience',
        labelFallbackEn: 'Overall Experience',
        labelFallbackTr: 'Genel Deneyim',
        legacyKeys: ['value_for_money'],
    },
    {
        key: 'itinerary_organization',
        labelKey: 'review_form.rating.itinerary_organization',
        labelFallbackEn: 'Itinerary & Organization',
        labelFallbackTr: 'Program ve Organizasyon',
        legacyKeys: ['destination'],
    },
    {
        key: 'accommodation_transport',
        labelKey: 'review_form.rating.accommodation_transport',
        labelFallbackEn: 'Accommodation & Transport',
        labelFallbackTr: 'Konaklama ve Ulaşım',
        legacyKeys: ['accommodation', 'transport'],
    },
    {
        key: 'tour_guide_staff',
        labelKey: 'review_form.rating.tour_guide_staff',
        labelFallbackEn: 'Tour Guide & Staff',
        labelFallbackTr: 'Rehber ve Ekip',
        legacyKeys: [],
    },
] as const;

export function reviewCategoryLabel(
    category: (typeof PRODUCT_REVIEW_RATING_CATEGORIES)[number],
    language: string | undefined,
    t: (key: string, fallback: string) => string,
): string {
    const isTr = language === 'tr' || (language?.startsWith('tr') ?? false);
    const fallback = isTr ? category.labelFallbackTr : category.labelFallbackEn;
    return t(category.labelKey, fallback);
}

export type ProductReviewRatingCategoryKey =
    (typeof PRODUCT_REVIEW_RATING_CATEGORIES)[number]['key'];

export type RatingCategoriesRecord = Partial<Record<string, number>>;

export function emptyProductReviewRatings(): Record<ProductReviewRatingCategoryKey, number | null> {
    return {
        overall_experience: null,
        itinerary_organization: null,
        accommodation_transport: null,
        tour_guide_staff: null,
    };
}

export function resolveCategoryRating(
    categories: RatingCategoriesRecord | null | undefined,
    categoryKey: string,
    legacyKeys: readonly string[],
): number | undefined {
    if (!categories) {
        return undefined;
    }

    const direct = categories[categoryKey];
    if (typeof direct === 'number' && direct > 0) {
        return direct;
    }

    if (categoryKey === 'accommodation_transport') {
        const legacyValues = legacyKeys
            .map((k) => categories[k])
            .filter((v): v is number => typeof v === 'number' && v > 0);
        if (legacyValues.length > 0) {
            return legacyValues.reduce((a, b) => a + b, 0) / legacyValues.length;
        }
    }

    for (const legacyKey of legacyKeys) {
        const legacy = categories[legacyKey];
        if (typeof legacy === 'number' && legacy > 0) {
            return legacy;
        }
    }

    return undefined;
}

export function collectRatingsForAverage(
    categories: RatingCategoriesRecord | null | undefined,
): number[] {
    const values: number[] = [];
    for (const cat of PRODUCT_REVIEW_RATING_CATEGORIES) {
        const rating = resolveCategoryRating(categories, cat.key, cat.legacyKeys);
        if (rating !== undefined) {
            values.push(rating);
        }
    }
    return values;
}
